import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './utils/api';

const RISK_COLORS = {
  HIGH: 'bg-red-100 text-red-700 border-red-300',
  MED: 'bg-orange-100 text-orange-700 border-orange-300',
  LOW: 'bg-green-100 text-green-700 border-green-300',
};

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  issued: 'bg-blue-100 text-blue-700',
};

function RamsTab({ jobId }) {
  const [rams, setRams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSiteHistory, setShowSiteHistory] = useState(false);
  const [siteHistory, setSiteHistory] = useState([]);
  const [aiAugmenting, setAiAugmenting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [acceptedRisks, setAcceptedRisks] = useState([]);
  const [acceptedSteps, setAcceptedSteps] = useState([]);
  const [acceptedPpe, setAcceptedPpe] = useState([]);

  const fetchRams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/jobs/${jobId}/rams`);
      if (res.ok) {
        const data = await res.json();
        setRams(data);
      }
    } catch (err) {
      console.error('Error fetching RAMS:', err);
    }
    setLoading(false);
  }, [jobId]);

  useEffect(() => { fetchRams(); }, [fetchRams]);

  const handleGenerate = async () => {
    try {
      const res = await apiFetch(`/api/jobs/${jobId}/rams/auto-generate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setRams(data);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to generate RAMS');
      }
    } catch (err) {
      alert('Error generating RAMS: ' + err.message);
    }
  };

  const handleIssue = async () => {
    if (!window.confirm('Issue this RAMS? It will be locked and a PDF generated.')) return;
    setIssuing(true);
    try {
      const res = await apiFetch(`/api/jobs/${jobId}/rams/issue`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setRams(prev => ({ ...prev, status: 'issued', pdf_path: data.pdf_path }));
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to issue RAMS');
      }
    } catch (err) {
      alert('Error issuing: ' + err.message);
    }
    setIssuing(false);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await apiFetch('/api/files/grant', {
        method: 'POST',
        body: JSON.stringify({ path: `/api/jobs/${jobId}/rams/pdf` }),
      });
      const data = await res.json();
      if (!res.ok || !data.grant) {
        alert(data.error || 'Could not open the PDF');
        return;
      }
      window.open(`/api/jobs/${jobId}/rams/pdf?grant=${encodeURIComponent(data.grant)}`, '_blank');
    } catch (err) {
      alert('Error downloading: ' + err.message);
    }
  };

  const handleAiAugment = async () => {
    setAiAugmenting(true);
    setAiError(null);
    setAiSuggestions(null);
    setAcceptedRisks([]);
    setAcceptedSteps([]);
    setAcceptedPpe([]);
    try {
      const res = await apiFetch(`/api/rams/${rams.id}/ai-augment`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI augment failed');
      setAiSuggestions(data.suggestions);
      // Pre-check all suggestions by default
      setAcceptedRisks(data.suggestions.additional_risks || []);
      setAcceptedSteps(data.suggestions.additional_method_steps || []);
      setAcceptedPpe(data.suggestions.additional_ppe || []);
    } catch (err) {
      setAiError(err.message);
    }
    setAiAugmenting(false);
  };

  const handleApplyAiSuggestions = async () => {
    if (!window.confirm(`Apply ${acceptedRisks.length} risk(s), ${acceptedSteps.length} method step(s), ${acceptedPpe.length} PPE item(s) to this RAMS? They will be marked as AI-suggested.`)) return;
    try {
      const res = await apiFetch(`/api/rams/${rams.id}/ai-augment/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepted_risks: acceptedRisks,
          accepted_method_steps: acceptedSteps,
          accepted_ppe: acceptedPpe,
          site_notes: aiSuggestions?.site_notes || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply suggestions');
      setAiSuggestions(null);
      fetchRams(); // refresh
    } catch (err) {
      alert('Error applying AI suggestions: ' + err.message);
    }
  };

  const toggleRiskAccept = (risk) => {
    setAcceptedRisks(prev => {
      const exists = prev.find(r => r.hazard === risk.hazard);
      if (exists) return prev.filter(r => r.hazard !== risk.hazard);
      return [...prev, risk];
    });
  };
  const toggleStepAccept = (step) => {
    setAcceptedSteps(prev => {
      const exists = prev.find(s => s.description === step.description);
      if (exists) return prev.filter(s => s.description !== step.description);
      return [...prev, step];
    });
  };
  const togglePpeAccept = (item) => {
    setAcceptedPpe(prev => {
      const exists = prev.find(p => p.item === item.item);
      if (exists) return prev.filter(p => p.item !== item.item);
      return [...prev, item];
    });
  };

  const handleFetchSiteHistory = async () => {
    if (!rams?.site_id) return;
    try {
      const res = await apiFetch(`/api/sites/${rams.site_id}/rams`);
      const data = await res.json();
      setSiteHistory(data);
      setShowSiteHistory(true);
    } catch (err) {
      console.error('Error fetching site history:', err);
    }
  };

  const handleSave = async () => {
    try {
      const res = await apiFetch(`/api/jobs/${jobId}/rams`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: rams.title,
          equipment: rams.equipment,
          works_summary: rams.works_summary,
          assessed_by: rams.assessed_by,
          assessment_date: rams.assessment_date,
          scope_items: rams.scope_items,
          risks: rams.risks,
          method_steps: rams.method_steps,
          ppe: rams.ppe,
          equipment_list: rams.equipment_list || [],
          materials: rams.materials,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setRams(data);
        setEditMode(false);
      }
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
  };

  // ─── Edit helpers (only used in edit mode) ────────────
  const updateField = (field, value) => setRams(prev => ({ ...prev, [field]: value }));
  const updateScopeItem = (i, field, value) => {
    setRams(prev => { const items = [...prev.scope_items]; items[i] = { ...items[i], [field]: value }; return { ...prev, scope_items: items }; });
  };
  const updateRisk = (i, field, value) => {
    setRams(prev => { const risks = [...prev.risks]; risks[i] = { ...risks[i], [field]: value }; return { ...prev, risks }; });
  };
  const updateMethodStep = (i, field, value) => {
    setRams(prev => { const steps = [...prev.method_steps]; steps[i] = { ...steps[i], [field]: value }; return { ...prev, method_steps: steps }; });
  };
  const togglePpe = (i) => {
    setRams(prev => { const ppe = [...prev.ppe]; ppe[i] = { ...ppe[i], required: !ppe[i].required }; return { ...prev, ppe }; });
  };

  // ─── Render ───────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="text-gray-500">Loading RAMS…</div></div>;
  }

  if (!rams) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-gray-500 mb-4">No RAMS found for this job.</div>
        <button onClick={handleGenerate} className="px-6 py-2 bg-[#082547] text-white rounded-lg font-medium hover:bg-[#0a3060] transition">
          Generate RAMS
        </button>
      </div>
    );
  }

  const isDraft = rams.status === 'draft';
  const isIssued = rams.status === 'issued';

  // Group method steps by section
  const methodGroups = {};
  (rams.method_steps || []).forEach(step => {
    if (!methodGroups[step.section]) methodGroups[step.section] = [];
    methodGroups[step.section].push(step);
  });

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* ─── Status Bar ─────────────────────────────── */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <div className="text-lg font-bold text-[#082547]">{rams.document_ref}</div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[rams.status]}`}>
            {rams.status?.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {isDraft && (
            <>
              <button onClick={handleAiAugment} disabled={aiAugmenting} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50 text-sm">
                {aiAugmenting ? 'AI thinking…' : '✨ AI Augment'}
              </button>
              <button onClick={() => setEditMode(!editMode)} className="text-sm text-gray-500 hover:text-gray-700 transition">
                {editMode ? 'Cancel Edit' : 'Edit'}
              </button>
              {editMode && (
                <button onClick={handleSave} className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition">
                  Save Changes
                </button>
              )}
              <button onClick={handleIssue} disabled={issuing} className="px-6 py-2 bg-[#082547] text-white rounded-lg font-medium hover:bg-[#0a3060] transition disabled:opacity-50">
                {issuing ? 'Issuing…' : '✓ Issue & Generate PDF'}
              </button>
            </>
          )}
          {isIssued && (
            <button onClick={handleDownloadPdf} className="px-6 py-2 bg-[#EC7626] text-white rounded-lg font-medium hover:bg-[#d0651a] transition">
              ⬇ Download PDF
            </button>
          )}
          {rams.site_id && (
            <button onClick={handleFetchSiteHistory} className="text-sm text-gray-500 hover:text-gray-700 transition">
              Site History
            </button>
          )}
        </div>
      </div>

      {/* ─── Project Details ────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">1. Project Details</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><span className="text-gray-500">Site:</span> <span className="font-medium">{rams.site_name}</span></div>
          <div><span className="text-gray-500">Client:</span> <span className="font-medium">{rams.client_name}</span></div>
          <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{rams.site_address}</span></div>
          <div><span className="text-gray-500">Equipment:</span>
            {editMode ? <input type="text" value={rams.equipment || ''} onChange={e => updateField('equipment', e.target.value)} className="ml-2 px-2 py-0.5 border border-gray-300 rounded text-sm" /> : <span className="font-medium">{rams.equipment}</span>}
          </div>
          <div><span className="text-gray-500">Assessed by:</span>
            {editMode ? <input type="text" value={rams.assessed_by || ''} onChange={e => updateField('assessed_by', e.target.value)} className="ml-2 px-2 py-0.5 border border-gray-300 rounded text-sm" /> : <span className="font-medium">{rams.assessed_by}</span>}
          </div>
          <div className="col-span-2"><span className="text-gray-500">Works:</span>
            {editMode ? <textarea value={rams.works_summary || ''} onChange={e => updateField('works_summary', e.target.value)} rows={2} className="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-sm" /> : <span className="font-medium">{rams.works_summary}</span>}
          </div>
        </div>
      </div>

      {/* ─── Scope of Works ─────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">2. Scope of Works</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b border-gray-100"><th className="py-2 w-8">#</th><th className="py-2">Task</th><th className="py-2 w-40">Standard</th></tr></thead>
          <tbody>
            {(rams.scope_items || []).map((item, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="py-2 text-gray-400">{item.line_number}</td>
                <td className="py-2">
                  {editMode ? <input type="text" value={item.task} onChange={e => updateScopeItem(i, 'task', e.target.value)} className="w-full px-2 py-0.5 border border-gray-200 rounded text-sm" /> : item.task}
                </td>
                <td className="py-2 text-gray-600">
                  {editMode ? <input type="text" value={item.standard_ref || ''} onChange={e => updateScopeItem(i, 'standard_ref', e.target.value)} className="w-full px-2 py-0.5 border border-gray-200 rounded text-sm" /> : item.standard_ref}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ─── PPE ────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">3. PPE Required</h2>
        <div className="flex flex-wrap gap-4">
          {(rams.ppe || []).map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              {editMode ? (
                <input type="checkbox" checked={item.required} onChange={() => togglePpe(i)} className="w-5 h-5 accent-[#2D8659]" />
              ) : (
                <span className={`text-lg ${item.required ? 'text-[#2D8659]' : 'text-gray-300'}`}>{item.required ? '✓' : '○'}</span>
              )}
              <span className={`text-sm ${item.required ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{item.item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Risk Assessment ────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">4. Risk Assessment</h2>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-gray-500 border-b border-gray-100">
            <th className="py-2 w-10">Ref</th><th className="py-2">Hazard</th><th className="py-2 w-20">Who</th>
            <th className="py-2 w-16 text-center">Initial</th><th className="py-2">Control Measures</th><th className="py-2 w-16 text-center">Residual</th>
          </tr></thead>
          <tbody>
            {(rams.risks || []).map((risk, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="py-2 text-gray-400 font-mono">{risk.ref}</td>
                <td className="py-2">{editMode ? <textarea value={risk.hazard} onChange={e => updateRisk(i, 'hazard', e.target.value)} rows={2} className="w-full px-1 py-0.5 border border-gray-200 rounded text-xs" /> : risk.hazard}</td>
                <td className="py-2 text-gray-600">{editMode ? <input type="text" value={risk.who_at_risk || ''} onChange={e => updateRisk(i, 'who_at_risk', e.target.value)} className="w-full px-1 py-0.5 border border-gray-200 rounded text-xs" /> : risk.who_at_risk}</td>
                <td className="py-2 text-center">
                  {editMode ? (
                    <select value={risk.initial_risk} onChange={e => updateRisk(i, 'initial_risk', e.target.value)} className={`px-1 py-0.5 border rounded text-xs font-bold ${RISK_COLORS[risk.initial_risk]}`}>
                      <option value="LOW">LOW</option><option value="MED">MED</option><option value="HIGH">HIGH</option>
                    </select>
                  ) : <span className={`px-2 py-0.5 rounded text-xs font-bold ${RISK_COLORS[risk.initial_risk]}`}>{risk.initial_risk}</span>}
                </td>
                <td className="py-2">{editMode ? <textarea value={risk.control_measures} onChange={e => updateRisk(i, 'control_measures', e.target.value)} rows={3} className="w-full px-1 py-0.5 border border-gray-200 rounded text-xs" /> : risk.control_measures}</td>
                <td className="py-2 text-center">
                  {editMode ? (
                    <select value={risk.residual_risk} onChange={e => updateRisk(i, 'residual_risk', e.target.value)} className={`px-1 py-0.5 border rounded text-xs font-bold ${RISK_COLORS[risk.residual_risk]}`}>
                      <option value="LOW">LOW</option><option value="MED">MED</option><option value="HIGH">HIGH</option>
                    </select>
                  ) : <span className={`px-2 py-0.5 rounded text-xs font-bold ${RISK_COLORS[risk.residual_risk]}`}>{risk.residual_risk}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ─── Method Statement ───────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">5. Method Statement</h2>
        {Object.entries(methodGroups).map(([section, steps]) => (
          <div key={section} className="mb-4">
            <div className="text-sm font-bold text-[#EC7626] mb-2">{section}</div>
            {steps.map((step) => (
              <div key={step.step_number} className="flex items-start gap-2 mb-2">
                <span className="text-sm font-bold text-[#EC7626] flex-shrink-0">Step {step.step_number}:</span>
                {editMode ? (
                  <div className="flex-1 flex gap-2">
                    <input type="text" value={step.section} onChange={e => updateMethodStep(steps.findIndex(s => s.step_number === step.step_number), 'section', e.target.value)} className="w-28 px-1 py-0.5 border border-gray-200 rounded text-xs" />
                    <textarea value={step.description} onChange={e => updateMethodStep(steps.findIndex(s => s.step_number === step.step_number), 'description', e.target.value)} rows={2} className="flex-1 px-1 py-0.5 border border-gray-200 rounded text-sm" />
                  </div>
                ) : (
                  <span className="text-sm">{step.description}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ─── Equipment & Materials ──────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">6. Equipment & Materials</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-bold text-gray-500 mb-2">Equipment</div>
            <table className="w-full text-xs">
              <tbody>
                {(rams.equipment_list || []).map((eq, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 font-medium">{eq.item}</td>
                    <td className="py-1.5 text-gray-600">{eq.purpose}</td>
                  </tr>
                ))}
                {(rams.equipment_list || []).length === 0 && <tr><td className="py-2 text-gray-400">No equipment listed</td></tr>}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-xs font-bold text-gray-500 mb-2">Materials</div>
            <table className="w-full text-xs">
              <tbody>
                {(rams.materials || []).map((mat, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-500 w-16">{mat.quantity}</td>
                    <td className="py-1.5 font-medium">{mat.description}</td>
                  </tr>
                ))}
                {(rams.materials || []).length === 0 && <tr><td className="py-2 text-gray-400">No materials listed</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ─── Sign-Off ───────────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-bold text-[#082547] border-b-2 border-[#EC7626] pb-2 mb-4">7. Sign-Off</h2>
        <div className="flex justify-between">
          <div className="border border-gray-200 rounded p-4 text-center w-45%">
            <div className="text-gray-400 text-xs mb-8">Assessor</div>
            <div className="border-t border-gray-400 pt-1 font-medium text-sm">{rams.assessed_by}</div>
            <div className="text-xs text-gray-500">{rams.assessment_date ? new Date(rams.assessment_date).toLocaleDateString('en-GB') : ''}</div>
          </div>
          <div className="border border-gray-200 rounded p-4 text-center w-45%">
            <div className="text-gray-400 text-xs mb-8">Client / Site Representative</div>
            <div className="border-t border-gray-400 pt-1 text-gray-400 text-sm">Signature</div>
            <div className="text-xs text-gray-400">Name: ____________ Date: ____________</div>
          </div>
        </div>
      </div>

      {/* ─── Site History Modal ─────────────────────── */}
      {showSiteHistory && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowSiteHistory(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-[#082547]">RAMS History — {rams.site_name}</h3>
              <button onClick={() => setShowSiteHistory(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-4">
              {siteHistory.length === 0 ? (
                <p className="text-gray-500 text-sm">No other RAMS documents for this site.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200"><th className="text-left py-2">Ref</th><th className="text-left py-2">Title</th><th className="text-left py-2">Date</th><th className="text-left py-2">Status</th></tr></thead>
                  <tbody>
                    {siteHistory.map(h => (
                      <tr key={h.id} className={`border-b border-gray-100 ${h.id === rams.id ? 'bg-blue-50' : ''}`}>
                        <td className="py-2 font-mono text-xs">{h.document_ref}</td>
                        <td className="py-2">{h.title}</td>
                        <td className="py-2 text-xs">{h.assessment_date ? new Date(h.assessment_date).toLocaleDateString('en-GB') : ''}</td>
                        <td className="py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[h.status]}`}>{h.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RamsTab;
